import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom"; // Import Link
import "../styles/ViewAll.css";
import { Plus, Heart } from "lucide-react";
import { getAllProducts } from "../services/productService";
import { IMAGE_BASE_URL } from "../api/api";

function ViewAll() {
  const navigate = useNavigate();
  const [sortType, setSortType] = useState("default");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishlist, setWishlist] = useState([]);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        setLoading(true);
        const data = await getAllProducts();
        setProducts(data);
      } catch (err) {
        console.error("Failed to load products:", err);
        setError("Failed to load products. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllProducts();
  }, []);

  const handleSortChange = (e) => {
    setSortType(e.target.value);
  };

  // Sort products
  const sortedProducts = [...products].sort((a, b) => {
    const priceA = parseFloat(a.sale_price || a.price);
    const priceB = parseFloat(b.sale_price || b.price);

    if (sortType === "price-low-high") return priceA - priceB;
    if (sortType === "price-high-low") return priceB - priceA;
    if (sortType === "alphabetical") return a.name.localeCompare(b.name);
    return 0;
  });

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    console.log("🛒 Add to cart:", product.name);
    // Add your cart logic here
  };

  const toggleWishlist = (e, productId) => {
    e.stopPropagation();
    setWishlist(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  if (loading) {
    return (
      <div className="container my-5">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading all products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container my-5">
        <div className="error-state">
          <p>{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5 view-all-container">
      {/* Updated Breadcrumb with Link */}
      <p className="mb-4 routepath-title">
        <Link to="/" className="breadcrumb-link">Home</Link> / All Products
      </p>

      {/* Header */}
      <div className="view-all-header">
        <h1 className="view-all-title">All Products ({products.length})</h1>
      </div>

      {/* Sorting */}
      <div className="sorting-container">
        <select
          value={sortType}
          onChange={handleSortChange}
          className="sort-select"
        >
          <option value="default">Sort By</option>
          <option value="price-low-high">Price: Low to High</option>
          <option value="price-high-low">Price: High to Low</option>
          <option value="alphabetical">Alphabetical</option>
        </select>
      </div>

      {/* Products Grid */}
      {sortedProducts.length === 0 ? (
        <div className="empty-state">
          <p>No products found.</p>
        </div>
      ) : (
        <div className="products-grid">
          {sortedProducts.map((product) => {
            const primaryImage = product.images?.find((img) => img.is_primary);
            const imageUrl = primaryImage
              ? `${IMAGE_BASE_URL}storage/${primaryImage.image_path}`
              : "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

            const isWishlisted = wishlist.includes(product.id);

            return (
              <div
                key={product.id}
                className="product-card-view"
                onClick={() => handleProductClick(product.id)}
              >
                <div className="product-image-container">
                  <img
                    src={imageUrl}
                    alt={product.name}
                    className="product-image"
                  />

                  {/* Wishlist Button */}
                  <button
                    className={`wishlist-btn ${isWishlisted ? 'wishlisted' : ''}`}
                    onClick={(e) => toggleWishlist(e, product.id)}
                  >
                    <Heart
                      size={18}
                      fill={isWishlisted ? "#FD7E14" : "none"}
                      color={isWishlisted ? "#FD7E14" : "#666"}
                    />
                  </button>

                  {/* Add to Cart Button */}
                  <button
                    className="add-to-cart-btn"
                    onClick={(e) => handleAddToCart(e, product)}
                  >
                    <Plus size={20} color="white" />
                  </button>

                  {/* Sale Badge */}
                  {product.sale_price && (
                    <div className="sale-badge">
                      Sale
                    </div>
                  )}
                </div>

                <div className="product-info">
                  <h3 className="product-title">
                    {product.name.length > 60
                      ? product.name.substring(0, 60) + "..."
                      : product.name}
                  </h3>

                  {/* Price Display */}
                  <div className="product-price">
                    {product.sale_price ? (
                      <div className="price-container">
                        <span className="original-price">Rs.{product.price}</span>
                        <span className="sale-price">Rs.{product.sale_price}</span>
                        <span className="discount-badge">
                          {Math.round((1 - parseFloat(product.sale_price) / parseFloat(product.price)) * 100)}% OFF
                        </span>
                      </div>
                    ) : (
                      <span className="regular-price">Rs.{product.price}</span>
                    )}
                  </div>

                  {/* Category */}
                  <div className="product-category">
                    {product.sub_category?.category?.name || "Uncategorized"}
                  </div>

                  {/* Stock Status */}
                  <div className={`stock-status ${product.stock_quantity > 0 ? 'in-stock' : 'out-of-stock'}`}>
                    {product.stock_quantity <= 0 && 'Out of stock'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ViewAll;